import { StrictMode, useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";
import "./mockup.css";
import { ApiError, createApi, type Me } from "./lib/api.ts";
import { initLang, t } from "./lib/i18n.ts";
import { parseRoute } from "./lib/router.ts";
import { applyTheme, getThemePref, setThemePref, watchSystemTheme } from "./lib/theme.ts";
import { Ctx, Loading, useLang, type AppCtx } from "./ui/common.tsx";
import { AiStudio, Home, Library, Login, NewProject, Projects, Settings } from "./ui/pages.tsx";
import { Editor } from "./ui/editor.tsx";

initLang(); applyTheme(getThemePref()); watchSystemTheme();

const IC: Record<string, string> = {
  home: "M3 11l9-8 9 8M5 10v10h5v-6h4v6h5V10",
  folder: "M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z",
  film: "M4 4h16v16H4zM8 4v16M16 4v16M4 9h4M4 15h4M16 9h4M16 15h4",
  spark: "M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8zM19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z",
  image: "M4 5h16v14H4zM4 15l4-4 4 4 3-3 5 5M9 9h.01",
  shapes: "M7 3l4 7H3zM15 14h6v6h-6zM8 14a3 3 0 100 6 3 3 0 000-6z",
  grid: "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z",
  gear: "M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 13a7.5 7.5 0 000-2l2-1.6-2-3.4-2.4 1a7.5 7.5 0 00-1.7-1L15 3h-4l-.4 2.9a7.5 7.5 0 00-1.7 1l-2.4-1-2 3.4 2 1.6a7.5 7.5 0 000 2l-2 1.6 2 3.4 2.4-1a7.5 7.5 0 001.7 1L11 21h4l.4-2.9a7.5 7.5 0 001.7-1l2.4 1 2-3.4z",
  plus: "M12 5v14M5 12h14",
  search: "M11 4a7 7 0 100 14 7 7 0 000-14zM21 21l-5-5",
  user: "M12 12a4 4 0 100-8 4 4 0 000 8zM4 21a8 8 0 0116 0",
  sun: "M12 8a4 4 0 100 8 4 4 0 000-8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4",
  moon: "M21 13A9 9 0 1111 3a7 7 0 0010 10z",
  out: "M9 4H5a1 1 0 00-1 1v14a1 1 0 001 1h4M16 8l4 4-4 4M20 12H9",
};
function Ic({ n }: { n: string }) { return <svg className="ni" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={IC[n]} /></svg>; }
const SIDE = [["/", "nav.home", "home"], ["/projects", "nav.projects", "folder"], ["/new", "nav.studio", "film"], ["/ai", "nav.ai", "spark"], ["/library", "nav.library", "image"],
  ["/library?tab=elements", "nav.elements", "shapes"], ["/library?tab=models", "nav.templates", "grid"], ["/settings", "nav.settings", "gear"]] as const;
const BOTTOM = [["/", "nav.home", "home"], ["/projects", "nav.projects_short", "folder"], ["/new", "nav.new", "plus"], ["/library", "nav.library", "image"], ["/settings", "nav.profile", "user"]] as const;
const PRIVATE = new Set(["home", "projects", "new", "ai", "library", "settings", "editor"]);

function App() {
  useLang();
  const api = useMemo(() => createApi(), []);
  const [me, setMe] = useState(undefined as Me | null | undefined);
  const [netErr, setNetErr] = useState(false);
  const [loc, setLoc] = useState({ path: location.pathname, search: location.search });
  useEffect(() => { const f = () => setLoc({ path: location.pathname, search: location.search }); addEventListener("popstate", f); return () => removeEventListener("popstate", f); }, []);
  const go = useCallback((path: string, replace = false) => {
    history[replace ? "replaceState" : "pushState"](null, "", path);
    const u = new URL(path, location.origin); setLoc({ path: u.pathname, search: u.search }); scrollTo(0, 0);
  }, []);
  const refreshMe = useCallback(async () => { try { setMe(await api.me()); setNetErr(false); } catch (e) { if (e instanceof ApiError && e.status === 401) setMe(null); else setNetErr(true); } }, [api]);
  useEffect(() => { void refreshMe(); }, [refreshMe]);

  const route = parseRoute(loc.path, loc.search);
  useEffect(() => { if (me === null && PRIVATE.has(route.name)) go("/login?next=" + encodeURIComponent(loc.path + loc.search), true); }, [me, loc.path]);
  useEffect(() => { if (me && route.name === "login") go("/", true); }, [me, loc.path]);
  useEffect(() => { document.title = "Modila"; }, []);

  const ctx: AppCtx = { api, me: me ?? null, setMe, go, refreshMe };
  if (me === undefined) return <div className="page">{netErr ? <div className="alert err" role="alert">{t("err.network")} <button className="btn sm" onClick={() => void refreshMe()}>{t("retry")}</button></div> : <Loading />}</div>;

  if (route.name === "editor" && me) return <Ctx.Provider value={ctx}><Editor id={route.id!} /></Ctx.Provider>;
  let page;
  switch (route.name) {
    case "home": page = me && <Home />; break;
    case "login": page = !me && <Login next={route.query.get("next")} />; break;
    case "projects": page = me && <Projects />; break;
    case "new": page = me && <NewProject />; break;
    case "ai": page = me && <AiStudio />; break;
    case "library": page = me && <Library />; break;
    case "settings": page = me && <Settings />; break;
    default: page = <div className="page"><div className="empty">{t("err.not_found")} <a href="/" onClick={(e: any) => { e.preventDefault(); go("/"); }}>{t("nav.home")}</a></div></div>;
  }
  const here = loc.path + loc.search;
  const active = (h: string) => (h.includes("?") ? here === h : loc.path === h && !(h === "/library" && loc.search.includes("tab=")));
  const link = (h: string) => ({ href: h, onClick: (e: any) => { e.preventDefault(); go(h); }, "aria-current": active(h) ? ("page" as const) : undefined });
  const name = me ? (me.email.split("@")[0] || "Modila") : "";
  const dark = document.documentElement.dataset.theme !== "light";
  return (
    <Ctx.Provider value={ctx}>
      <div className={"shell" + (me ? " has-side" : "")}>
        {me && <aside className="sb" aria-label="Navigation">
          <a className="logo" href="/" onClick={(e: any) => { e.preventDefault(); go("/"); }}><b className="mk">M</b><span>MODILA</span></a>
          <nav>{SIDE.map(([h, k, i]) => <a key={h} {...link(h)}><Ic n={i} /><span>{t(k)}</span></a>)}</nav>
          <a className="usercard" {...link("/settings")}><span className="av">{name.slice(0, 1).toUpperCase()}</span>
            <span className="un"><strong>{name}</strong><small className="plan">{t(me.plan === "trial" ? "plan.trial" : "plan.free")}</small></span></a>
        </aside>}
        <div className="col">
          <header className="tb">
            {me ? <>
              <a className="logo m-only" href="/" onClick={(e: any) => { e.preventDefault(); go("/"); }}><b className="mk">M</b></a>
              <form className="search" role="search" onSubmit={(e: any) => { e.preventDefault(); const q = String(new FormData(e.target).get("q") ?? "").trim(); go(q ? "/projects?q=" + encodeURIComponent(q) : "/projects"); }}>
                <Ic n="search" /><input name="q" type="search" placeholder={t("nav.search")} aria-label={t("nav.search")} /></form>
              <button className="ibtn" aria-label={t("settings.theme")} onClick={() => { const n = dark ? "light" : "dark"; setThemePref(n); setLoc({ ...loc }); }}><Ic n={dark ? "sun" : "moon"} /></button>
              <a className="av tb-av" {...link("/settings")} aria-label={t("nav.profile")}>{name.slice(0, 1).toUpperCase()}</a>
            </> : <>
              <a className="logo" href="/" onClick={(e: any) => { e.preventDefault(); go("/"); }}><b className="mk">M</b><span>MODILA</span></a>
              <span className="grow" /><a className="btn sm" href="/login" onClick={(e: any) => { e.preventDefault(); go("/login"); }}>{t("nav.signin")}</a></>}
          </header>
          <main>{page}</main>
        </div>
        {me && <nav className="bn" aria-label="Navigation">{BOTTOM.map(([h, k, i]) => <a key={h} {...link(h)} className={i === "plus" ? "plus" : ""} aria-label={t(k)}><Ic n={i} /><span>{t(k)}</span></a>)}</nav>}
      </div>
    </Ctx.Provider>
  );
}
createRoot(document.getElementById("root")!).render(<StrictMode><App /></StrictMode>);
